<template>
  <div>
    <VDetailBoxWrapper gray tag="ul">
      <VDetailBox tag="li">
        <template #label>
          <!-- #S828 -->
          {{ $t('internalstock.usid_label') }}
        </template>
        <template #default>
          {{ id }}
        </template>
      </VDetailBox>

      <VDetailBox v-for="(name, key) in descriptions" :key="'detailbox_' + key" tag="li">
        <template #label>
          <!-- #D495 -->
          {{ $tm('internalstock.description_name_mapping_', key, true) }}
        </template>
        <template #default>
          {{ name }}
        </template>
      </VDetailBox>
      <VDetailBox v-if="status === 1 && subState === 1" icon="information" tag="li">
        <!-- #S858 -->
        {{ $t('internalstock.reserved_subscription_information') }}
      </VDetailBox>
    </VDetailBoxWrapper>

    <VDetailBoxWrapper>
      <!-- #S829 -->
      <VDetailBox v-if="isChargingInformationVisible">
        <template #headline>{{ $t('internalstock.charging_information_sub_headline') }}</template>

        <template #default>
          <!-- #D497 + #D498 ... -->
          <VDescriptionList>
            <VEntryButton
              v-for="(chargingInterval, index) in chargingIntervals"
              :key="index"
              :name="`${formatDate(chargingInterval.start)} - ${formatDate(chargingInterval.end)}`"
              label="internalstock.transaction_details_link_label"
              :hidden="!chargingInterval.from"
              :to="getTransactionLink(chargingInterval)"
            >
              {{ formatCurrency(chargingInterval.price, chargingInterval.currency) }}
            </VEntryButton>
            <VEntry v-if="isNextRegularPaymentVisible" name="internalstock.next_charging_label">
              {{ formatDate(nextRegularPayment) }}
            </VEntry>
          </VDescriptionList>
        </template>
      </VDetailBox>

      <VDetailBox :width="2 + (isChargingInformationVisible ? 0 : 1)">
        <template #headline>{{ $t('internalstock.additional_information_sub_headline') }}</template>
        <template #default>
          <VDescriptionListDivided>
            <VEntry name="internalstock.subscription_template_id_label" :value="item.id" />
            <VEntry v-if="servicedContractIdentifierType" name="internalstock.charged_contract_label">
              {{ servicedContractIdentifierType }}: {{ servicedContractIdentifierValue }}
            </VEntry>
            <VEntry name="internalstock.service_portal_label" :value="servicePortal" />
            <VEntry v-if="precedingSubscriptionId" name="internalstock.preceding_subscription_label">
              {{ precedingSubscriptionTemplateName }} ({{ precedingSubscriptionTemplateId }}), {{ precedingSubscriptionId }}
            </VEntry>
            <VEntry v-if="cancellationTime" name="internalstock.earliest_cancellation_date_label">
              {{ formatDate(cancellationTime) }}
            </VEntry>
            <VEntry v-if="followupSubscriptionId" name="internalstock.succeeding_product_label">
              {{ followupSubscriptionTemplateName }} ({{ followupSubscriptionTemplateId }}), {{ followupSubscriptionId }}
            </VEntry>
          </VDescriptionListDivided>
        </template>
      </VDetailBox>

      <VBundleInstance v-for="(bundleInstance, index) in bundleInstances" :key="'instance_' + index" :instance="bundleInstance" :index="index" />
    </VDetailBoxWrapper>

    <div class="offset-left-2 offset-right-2 offset-top-2 offset-bottom-1 text-right">
      <button v-if="[2, 3].includes(status) && !end" class="btn btn-small btn-default" :disabled="grid.isBusy" @click="onConfirmation(item.id, id, 'cancel')">
        <!-- #S852 -->
        {{ $t('internalstock.cancel_button_label') }}
      </button>

      <button
        v-if="[2, 3].includes(status) && end"
        class="btn btn-small btn-default"
        :disabled="grid.isBusy"
        @click="onConfirmation(item.id, id, 'reactivate')"
      >
        <!-- #S872 -->
        {{ $t('internalstock.reactivate_button_label') }}
      </button>

      <button v-if="[2, 3, 4].includes(status)" class="btn btn-small btn-default" :disabled="grid.isBusy" @click="onConfirmation(item.id, id, 'terminate')">
        <!-- #S853 -->
        {{ $t('internalstock.terminate_button_label') }}
      </button>

      <button
        v-if="status === 1 && subState === 1"
        class="btn btn-small btn-default"
        :disabled="grid.isBusy"
        @click="onConfirmation(item.id, id, 'terminateReservation')"
      >
        <!-- #S859 -->
        {{ $t('internalstock.terminateReservation_button_label') }}
      </button>
    </div>

    <VConfirmation
      :id="`${item.id}_${id}`"
      v-model="grid.confirmation"
      :heading="actionHeadline"
      :is-busy="grid.isBusy"
      class="row"
      @confirm="$emit('confirm', $event)"
    >
      <template #prompt="{ action }">
        <template v-if="action === 'cancel'">
          <template v-if="cancellationTime">
            {{ $t('internalstock.cancel_with_date_prompt', { cancellationTime: formatDate(cancellationTime) }) }}
          </template>
          <template v-else>
            {{ $t('internalstock.cancel_prompt') }}
          </template>
        </template>
        <template v-else>
          {{ $t(`internalstock.${action}_prompt`) }}
        </template>
      </template>

      <template #success="{ action }">
        <!-- #S873 -->
        {{ $t(`internalstock.${action}_successful_information`) }}
      </template>
    </VConfirmation>
  </div>
</template>

<script>
import { formatDate, formatDateUrl } from '@/utils/date';
import FormatCurrencyMixin from '@/mixins/formatCurrency';
import { PAYMENT_TRANSACTIONS_OVERVIEW, PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS } from '@route-types';
import VConfirmation from '@/components/Confirmation';
import VDescriptionList from '@/components/DescriptionList';
import VDescriptionListDivided from '@/components/DescriptionList/Divided';
import VEntry from '@/components/DescriptionList/Entry';
import VEntryButton from '@/components/DescriptionList/EntryButton';

export default {
  name: 'InternalStockItemDetails',

  components: {
    VConfirmation,
    VBundleInstance: () => import('@/components/Substore/BundleInstance'),
    VDetailBox: () => import('@/components/UI/DetailBox'),
    VDetailBoxWrapper: () => import('@/components/UI/DetailBox/Wrapper'),
    VDescriptionList,
    VDescriptionListDivided,
    VEntry,
    VEntryButton,
  },

  mixins: [FormatCurrencyMixin],

  inject: {
    grid: {
      from: 'grid',
      default: {},
    },
  },

  props: {
    item: {
      type: Object,
      required: true,
    },

    id: {
      type: String,
      required: true,
    },

    status: {
      type: Number,
      required: true,
    },

    subState: {
      type: Number,
      required: false,
      default: null,
    },

    end: {
      type: String,
      required: false,
      default: null,
    },

    cancellationTime: {
      type: String,
      required: false,
      default: null,
    },

    descriptions: {
      type: Object,
      required: false,
      default: null,
    },

    chargingIntervals: {
      type: Array,
      required: false,
      default: null,
    },

    optinPossible: {
      type: Boolean,
      required: false,
    },

    nextRegularPayment: {
      type: String,
      required: false,
      default: null,
    },

    servicedContractIdentifierType: {
      type: String,
      required: false,
      default: null,
    },

    servicedContractIdentifierValue: {
      type: String,
      required: false,
      default: null,
    },

    servicePortal: {
      type: String,
      required: false,
      default: null,
    },

    precedingSubscriptionTemplateName: {
      type: String,
      required: false,
      default: null,
    },

    precedingSubscriptionTemplateId: {
      type: String,
      required: false,
      default: null,
    },

    precedingSubscriptionId: {
      type: String,
      required: false,
      default: null,
    },

    followupSubscriptionTemplateId: {
      type: String,
      required: false,
      default: null,
    },

    followupSubscriptionTemplateName: {
      type: String,
      required: false,
      default: null,
    },

    followupSubscriptionId: {
      type: String,
      required: false,
      default: null,
    },

    bundleInstances: {
      type: Array,
      required: false,
      default: null,
    },
  },

  computed: {
    isChargingInformationVisible() {
      return !this.optinPossible && ((this.chargingIntervals && this.chargingIntervals.length) || this.isNextRegularPaymentVisible);
    },

    isNextRegularPaymentVisible() {
      return [2, 3].includes(this.status) && this.nextRegularPayment;
    },

    actionHeadline() {
      return this.grid.confirmation?.headline;
    },
  },

  beforeDestroy() {
    this.grid.confirmation = null;
  },

  methods: {
    formatDate,

    onConfirmation(subscriptionTemplateId, uniqueSubscriptionId, action) {
      this.grid.confirmation = {
        id: `${subscriptionTemplateId}_${uniqueSubscriptionId}`,
        uniqueSubscriptionId,
        action,
        headline: `internalstock.${action}_button_label`,
      };
    },

    getTransactionLink({ transactionId, from, to }) {
      from = formatDateUrl(from);
      to = formatDateUrl(to);
      if (transactionId) {
        return { name: PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS, params: { transactionId, from, to } };
      }
      return { name: PAYMENT_TRANSACTIONS_OVERVIEW, params: { from, to } };
    },
  },
};
</script>
